// QuickCrewAdd (George 2026-09-22: "Still wonky trying to add an employee,
// takes you to a screen that takes you to a screen and you have to make
// several clicks.") — ONE screen to put a person on a job: type the name,
// pick where they go, Add. Opens from the Allocations picker row, the
// Allocations drill-in header and the Job Hub's Staffing card.
//
// It adds nothing new to the write side. An open ESTIMATE slot saves through
// the Crew editor's PUT /workforce/crew-editor (savePersonnelCore + the
// forward sync), so the estimate — George's PLAN — is what gets updated and
// the Allocations row follows with origin='estimate'. Everything else is a
// plain POST /workforce/allocations: beyond plan on an estimate job, the
// normal path on an ops-manual job. Dates / per diem / travel / verifications
// on the new row stay editable on the Allocations drill-in, as always.
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { styles, fmtDate } from './UI';
import { buildCrewSlots, mdDate } from './CrewEditor';
import { jobWindow, newAllocationDefaults } from '../utils/allocationDefaults';

const SHIFTS = ['Day', 'Night', 'Swing'];

export default function QuickCrewAdd({ projectId, job, employees: employeesProp, onClose, onSaved }) {
  const [crew, setCrew] = useState(null);        // GET /crew-editor payload
  const [reqs, setReqs] = useState([]);          // job_craft_requirements for this job
  const [allocs, setAllocs] = useState([]);      // job_allocations for this job
  const [roster, setRoster] = useState(employeesProp || []);
  const [crafts, setCrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [name, setName] = useState('');
  const [target, setTarget] = useState('');
  const [shift, setShift] = useState('Day');
  const [craftPick, setCraftPick] = useState('');
  const win = jobWindow(job);
  const [startDate, setStartDate] = useState(win.start);
  const [endDate, setEndDate] = useState(win.end);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [added, setAdded] = useState([]);

  const load = async () => {
    const [c, r, a] = await Promise.all([
      api.getCrewEditor(projectId),
      api.listJobRequirements(projectId),
      api.listAllocations({ project_id: projectId }),
    ]);
    setCrew(c);
    setReqs(Array.isArray(r) ? r : []);
    setAllocs(Array.isArray(a) ? a : []);
  };
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr('');
    setTarget('');   // re-picked from the LOADED targets below, never from the empty pre-load list
    load().catch(e => { if (!cancelled) setErr(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    if (!employeesProp) api.listEmployees().then(rows => { if (!cancelled) setRoster(rows); }).catch(() => {});
    api.listCrafts().then(rows => { if (!cancelled) setCrafts(rows); }).catch(() => {});
    return () => { cancelled = true; };
  }, [projectId]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (employeesProp) setRoster(employeesProp); }, [employeesProp]);

  const hasEstimate = !!(crew && crew.has_estimate);
  const { slots, init } = useMemo(
    () => (hasEstimate ? buildCrewSlots(crew) : { slots: [], init: {} }),
    [crew, hasEstimate]
  );
  const openSlots = useMemo(
    () => slots.filter(s => !((init[s.key] || {}).name || '').trim()),
    [slots, init]
  );

  // Plan crafts with seats still open (ops-manual jobs — on an estimate job
  // the open seats ARE the estimate slots above).
  const filledByCraft = useMemo(() => {
    const m = new Map();
    for (const a of allocs) m.set(a.craft, (m.get(a.craft) || 0) + 1);
    return m;
  }, [allocs]);
  const openPlan = useMemo(() => reqs
    .map(r => ({ craft: r.craft, open: Math.max(0, (r.needed || 0) - (filledByCraft.get(r.craft) || 0)) }))
    .filter(r => r.open > 0), [reqs, filledByCraft]);

  // Targets: estimate slots first (they update the plan), then the free
  // allocation path. `kind` decides the write path in add().
  const targets = useMemo(() => {
    const out = [];
    if (hasEstimate) {
      for (const s of openSlots) {
        const span = s.phases.length
          ? `${mdDate(s.phases[0].start_date)} → ${mdDate(s.phases[s.phases.length - 1].end_date)}`
          : 'no dated phases';
        out.push({ key: `slot:${s.key}`, kind: 'slot', slot: s,
          label: `${s.estimate_label ? `${s.estimate_label} · ` : ''}${s.craft} · ${s.shift}${s.rotates ? ' ↔' : ''} · ${span}`,
          hint: `estimate slot L${s.line_no}` });
      }
      out.push({ key: 'beyond', kind: 'free', label: 'Beyond plan — outside the estimate crew',
        hint: 'a plain assignment the estimate sync never touches' });
    } else {
      for (const r of openPlan) {
        out.push({ key: `plan:${r.craft}`, kind: 'plan', craft: r.craft,
          label: `${r.craft} · ${r.open} open` });
      }
      out.push({ key: 'other', kind: 'free', label: openPlan.length ? 'Another craft' : 'Pick a craft' });
    }
    return out;
  }, [hasEstimate, openSlots, openPlan]);
  // Default = the first OPEN seat once the data is in (while loading, the
  // only target is the free-craft fallback and it must not stick). After an
  // add, the current pick survives if it still exists — the next open slot
  // of the same craft is usually the next person's.
  useEffect(() => {
    if (loading || !targets.length) return;
    if (!targets.find(t => t.key === target)) setTarget(targets[0].key);
  }, [loading, targets, target]);
  const t = targets.find(x => x.key === target) || null;
  const needsCraft = t && t.kind === 'free';
  const isSlot = t && t.kind === 'slot';

  const activeRoster = useMemo(() => (roster || [])
    .filter(e => e.is_active !== false && e.full_name)
    .sort((a, b) => a.full_name.localeCompare(b.full_name)), [roster]);
  const byName = useMemo(() => {
    const m = {};
    for (const e of activeRoster) m[e.full_name.trim().toLowerCase()] = e;
    return m;
  }, [activeRoster]);
  const emp = byName[name.trim().toLowerCase()] || null;
  const onJobNow = allocs.filter(a => !a.separated_at).map(a => a.employee_name).filter(Boolean);
  const craftForAdd = t ? (t.kind === 'plan' ? t.craft : t.kind === 'free' ? craftPick : t.slot.craft) : '';
  const canAdd = !!t && !!name.trim() && !busy && (isSlot || !!emp) && (!needsCraft || !!craftPick);

  const add = async () => {
    if (!canAdd) return;
    setBusy(true);
    setErr('');
    setNote('');
    const who = emp ? emp.full_name : name.trim();
    try {
      let line;
      if (isSlot) {
        // Same payload the Crew editor sends: EVERY slot, saved names intact,
        // the chosen slot filled in. Contact detail belongs to the person.
        const rows = slots.map(s => {
          const v = init[s.key] || { name: '', email: '', phone: '' };
          const mine = s.key === t.slot.key;
          return {
            labor_line_id: s.labor_line_id, shift: s.shift, slot_index: s.slot_index,
            name: mine ? who : (v.name || ''),
            email: mine ? ((emp && emp.email) || '') : (v.email || ''),
            phone: mine ? ((emp && emp.phone) || '') : (v.phone || ''),
          };
        });
        const r = await api.saveCrewEditor(projectId, rows);
        const sy = (r && r.sync) || {};
        line = `✓ ${who} → ${t.slot.craft} (${t.slot.shift}) · estimate updated, assignments synced: +${sy.added || 0}`;
      } else {
        await api.createAllocation(newAllocationDefaults({
          project_id: projectId, employee: emp, craft: craftForAdd, shift,
          start_date: startDate, end_date: endDate,
        }));
        line = `✓ ${who} → ${craftForAdd} (${shift})${hasEstimate ? ' · beyond plan' : ''}`;
      }
      setAdded(prev => [...prev, who]);
      setName('');
      setNote(line);
      await load();
      if (onSaved) onSaved();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const close = () => { if (!busy) onClose(); };
  const radio = (opt) => (
    <label key={opt.key} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '6px 8px', borderRadius: 6,
      background: target === opt.key ? '#FFF7ED' : 'transparent', cursor: 'pointer', fontSize: 13 }}>
      <input type="radio" name="quick-crew-target" checked={target === opt.key} onChange={() => setTarget(opt.key)} style={{ marginTop: 3 }} />
      <span>
        {opt.label}
        {opt.hint && <span style={{ display: 'block', fontSize: 11, color: '#6B7280' }}>{opt.hint}</span>}
      </span>
    </label>
  );

  return (
    <div onClick={close} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1200,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...styles.card, width: 600, maxWidth: '96vw', maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>
            + Add crew
            <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
              · <span style={{ fontFamily: 'monospace', color: '#E36F2C', fontWeight: 700 }}>{job.job_number || '(no job #)'}</span>
              {job.site ? ` — ${job.site}` : ''}
            </span>
          </h3>
          <button onClick={close} style={{ ...styles.btnSecondary, fontSize: 12, padding: '4px 10px' }}>✕</button>
        </div>
        <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
          Job window {win.start ? fmtDate(win.start) : '—'} → {win.end ? fmtDate(win.end) : '—'}
          {' · '}{onJobNow.length ? `on the job now: ${onJobNow.join(', ')}` : 'no one on the job yet'}
        </div>

        {loading ? (
          <div style={{ padding: 20, color: '#6B7280' }}>Loading…</div>
        ) : (
          <>
            <div style={{ marginTop: 14 }}>
              <label style={styles.label}>Who</label>
              <input list="quick-crew-roster" autoFocus style={{ ...styles.input, width: '100%' }} value={name}
                placeholder={`Type a name from the roster (${activeRoster.length} active)…`}
                onChange={e => setName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
              <datalist id="quick-crew-roster">
                {activeRoster.map(e => <option key={e.id} value={e.full_name} />)}
              </datalist>
              {name.trim() && (
                emp ? (
                  <div style={{ fontSize: 12, color: '#065F46', marginTop: 4 }}>
                    ✓ {emp.full_name}{emp.employee_no ? ` · employee # ${emp.employee_no}` : ''}
                    {onJobNow.includes(emp.full_name) ? ' · already on this job' : ''}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: '#92400E', marginTop: 4 }}>
                    {isSlot
                      ? '⚠ Not on the roster — will be added as a new person when the slot saves (same as the Crew editor).'
                      : '⚠ Not on the roster — pick a roster name, or add them on the Roster tab first.'}
                  </div>
                )
              )}
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={styles.label}>
                Where
                <span style={{ fontWeight: 400, marginLeft: 6 }}>
                  {hasEstimate
                    ? (openSlots.length
                        ? `· ${openSlots.length} open estimate slot${openSlots.length === 1 ? '' : 's'} — filling one updates the estimate (the plan)`
                        : '· every estimate slot is filled — this adds beyond the plan; more seats means revising the estimate headcount')
                    : (openPlan.length ? '· open seats on the staffing plan' : '· no open seats on the plan')}
                </span>
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 220, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 6, padding: 4 }}>
                {targets.map(radio)}
              </div>
            </div>

            {t && t.kind !== 'slot' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginTop: 10 }}>
                {needsCraft && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={styles.label}>Craft *</label>
                    <select style={{ ...styles.input, width: '100%' }} value={craftPick} onChange={e => setCraftPick(e.target.value)}>
                      <option value="">— Pick craft —</option>
                      {crafts.filter(c => c.is_active !== false).map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label style={styles.label}>Shift</label>
                  <select style={{ ...styles.input, width: '100%' }} value={shift} onChange={e => setShift(e.target.value)}>
                    {SHIFTS.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label style={styles.label}>Start</label>
                  <input type="date" min="2000-01-01" max="2100-12-31" style={{ ...styles.input, width: '100%' }} value={startDate} onChange={e => setStartDate(e.target.value)} />
                </div>
                <div>
                  <label style={styles.label}>End</label>
                  <input type="date" min="2000-01-01" max="2100-12-31" style={{ ...styles.input, width: '100%' }} value={endDate} onChange={e => setEndDate(e.target.value)} />
                </div>
              </div>
            )}

            {err && <div style={{ ...styles.errorBox, marginTop: 10 }}>{err}</div>}
            {note && !err && <div style={{ marginTop: 10, fontSize: 13, color: '#065F46', fontWeight: 600 }}>{note}</div>}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: '#6B7280' }}>
                {added.length ? `${added.length} added this round. ` : ''}
                Per diem, travel and verifications for the new row: Allocations → the job → Edit.
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={styles.btnSecondary} onClick={close} disabled={busy}>{added.length ? 'Done' : 'Cancel'}</button>
                <button style={styles.btn} onClick={add} disabled={!canAdd}>{busy ? 'Adding…' : 'Add'}</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
